/** Formatação por locale. Tudo que o usuário lê passa por aqui.
 *
 * O locale não é preferência de estilo: é o que decide se a agenda mostra
 * `14:30` ou `2:30 PM`, e se a data 03/09 é 3 de setembro ou 9 de março.
 * A agenda é uma tela de horas — ler a hora errada é perder o carro.
 *
 * `Intl` resolve os três (moeda, data, relógio) a partir do mesmo código,
 * então não há tabela nossa de formato: só o locale certo chegando aqui.
 */

/** Moeda e locale vêm de fora — a moeda do payload, o locale de quem lê.
 *
 * ## Por que não há default aqui
 *
 * Havia: `currency = "BRL"`, `locale = "pt-BR"`. Existiam como ponte para
 * as telas que ainda não passavam os dois, e foi exatamente o que
 * aconteceu — oito arquivos continuaram chamando `money(preco)` e a
 * oficina americana viu "R$ 89,00" no lugar de "$89.00", com o número
 * certo e a moeda errada. Ninguém percebe isso lendo o diff: compila,
 * roda, e mente.
 *
 * Sem default, esquecer o locale deixa de compilar. É a mesma escolha do
 * `pt: Record<Chave, string>` em i18n.ts, e pela mesma razão: o erro que
 * o compilador pega não chega à tela de ninguém.
 *
 * Na prática ninguém chama isto diretamente — `useFormat()` já entrega
 * tudo amarrado ao idioma e à moeda da oficina.
 *
 * O par locale+moeda importa junto. `Intl` com locale "pt-BR" e moeda USD
 * escreve "US$ 46,00"; com "en-US", "$46.00". Os dois estão certos para
 * leitores diferentes, e é o leitor quem decide. */
export const money = (value: string | number, currency: string, locale: string) =>
  new Intl.NumberFormat(locale, { style: "currency", currency }).format(
    typeof value === "string" ? Number(value) : value,
  );

/** Hora local da oficina. O backend já devolve com o offset correto,
 *  então basta não reinterpretar o fuso aqui.
 *
 *  `2:30 PM` em en-US e `14:30` em pt-BR saem do mesmo `Intl` — não há
 *  `if` de 12h/24h em lugar nenhum. */
export const hhmm = (iso: string, locale: string) =>
  new Date(iso).toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });

export const dayLabel = (iso: string, locale: string) =>
  new Date(iso).toLocaleDateString(locale, {
    weekday: "short",
    day: "2-digit",
    month: "2-digit",
  });

export const fullDate = (iso: string, locale: string) =>
  new Date(iso).toLocaleDateString(locale, {
    weekday: "long",
    day: "2-digit",
    month: "long",
  });

/** "1h30" / "1h 30m". O formato curto de duração não tem `Intl` que
 *  resolva bem em 2026, então é tabela — e ela vive no dicionário. */
export const duration = (minutes: number, locale: string) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (minutes < 60) return locale.startsWith("en") ? `${minutes} min` : `${minutes}min`;
  if (!m) return `${h}h`;
  return locale.startsWith("en") ? `${h}h ${m}m` : `${h}h${String(m).padStart(2, "0")}`;
};

/** Nome do dia da semana, 0 = segunda — a convenção de `WorkingHours.weekday`.
 *
 *  A tabela escrita à mão que existia aqui mostrava "Segunda" na tela de
 *  uma oficina do Texas. `Intl` já traz os sete nomes em todo idioma que
 *  o navegador conhece; basta ancorar num domingo real e andar os dias.
 *
 *  2024-01-01 foi uma segunda-feira. Meio-dia UTC para que nenhum fuso
 *  do mundo empurre a data para o dia anterior. */
export const weekdayName = (
  index: number,
  locale: string,
  style: "long" | "short" = "long",
) =>
  new Date(Date.UTC(2024, 0, 1 + index, 12)).toLocaleDateString(locale, {
    weekday: style,
    timeZone: "UTC",
  });

/** "14:30" → "2:30 PM" onde o relógio é de 12 horas.
 *
 *  Recebe a hora como ela é guardada (HH:MM, 24h) e devolve como se lê.
 *  A data é descartável: só existe porque `Intl` formata instantes, não
 *  horas soltas. */
export const clockLabel = (hhmmStr: string, locale: string) => {
  const [h, m] = hhmmStr.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return hhmmStr;
  return new Date(Date.UTC(2024, 0, 1, h, m)).toLocaleTimeString(locale, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC",
  });
};

/** O separador decimal deste idioma: "," em pt-BR, "." em en-US.
 *
 *  Descoberto pelo `Intl` e não por uma tabela: é a mesma pergunta que
 *  `money()` já responde, e duas respostas diferentes na mesma tela é
 *  como "$ 89,00" aparece. */
export const decimalSeparator = (locale: string) =>
  new Intl.NumberFormat(locale).formatToParts(1.1).find((p) => p.type === "decimal")?.value ?? ".";

/** Número para dentro de um `<input>`, no separador de quem digita.
 *
 *  Sem moeda e sem agrupamento de milhar de propósito: o valor volta
 *  para o campo, e "1.234,00" com o símbolo junto não é editável. */
export const decimalInput = (value: string | number, locale: string) =>
  (typeof value === "string" ? Number(value) : value).toFixed(2).replace(".", decimalSeparator(locale));

/** O que o dono digitou de volta para o formato que a API entende.
 *
 *  Aceita os dois separadores em qualquer idioma: o teclado numérico do
 *  celular brasileiro tem ponto, e recusar "89.90" de um brasileiro
 *  travaria o cadastro por causa do teclado dele. */
export const parseDecimal = (texto: string) => texto.trim().replace(",", ".");

/** Quilometragem na unidade do país. O banco guarda km, sempre.
 *
 *  Converter na exibição e não no armazenamento é o que mantém o
 *  histórico comparável: o mesmo carro atendido em dois países teria dois
 *  números incompatíveis na mesma ficha. */
export const odometer = (km: number | null, unit: string, locale: string) => {
  if (km === null || km === undefined) return "—";
  if (unit === "mi") return `${Math.round(km * 0.621371).toLocaleString(locale)} mi`;
  return `${km.toLocaleString(locale)} km`;
};

/** +5511998887777 → (11) 99888-7777 · +13055550199 → (305) 555-0199
 *
 *  O código de área tem 2 dígitos no Brasil e 3 nos Estados Unidos.
 *  Aplicar a máscara errada não quebra nada — só mostra o telefone do
 *  cliente num formato que a recepção não reconhece. */
export const phone = (value: string, country: string) => {
  const digits = value.replace(/\D/g, "");
  if (country === "US") {
    const d = digits.replace(/^1/, "");
    if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
    return value;
  }
  const d = digits.replace(/^55/, "");
  if (d.length === 11) return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
  if (d.length === 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return value;
};

/** Placa como ela é guardada: MAIÚSCULAS, sem hífen e sem espaço.
 *
 * Mesma normalização de `apps/common/validators.normalize_plate`. A
 * recepção digita "abc-1d23" enquanto o cliente dita pelo telefone, e é
 * essa string que precisa virar a chave de busca.
 *
 * O limite vem da região: 7 no Brasil, 8 nos Estados Unidos. */
export const plateDigits = (value: string, maxLength: number = 7) =>
  value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase().slice(0, maxLength);

/** ABC-1D23 no Brasil; nos EUA, a placa como está no carro.
 *
 * Sem hífen lá de propósito: o formato varia por estado, e inventar um
 * separador exibiria a placa diferente do que a pessoa tem na mão. */
export const plateMask = (value: string, country: string) => {
  const p = plateDigits(value, country === "US" ? 8 : 7);
  if (country === "US") return p;
  return p.length > 3 ? `${p.slice(0, 3)}-${p.slice(3)}` : p;
};

/** No Brasil, o padrão antigo (ABC1234) e o Mercosul (ABC1D23).
 *
 * Nos Estados Unidos não há padrão nacional: 2 a 8 alfanuméricos, sem
 * dígito verificador. A conferência lá pega dedo escorregado, não placa
 * inexistente — essa validação não existe.
 *
 * Duplicar a regra no cliente é intencional: a placa errada só apareceria
 * depois do POST, e quem digita está com o cliente na frente. O backend
 * continua sendo a autoridade (apps/common/regions.py). */
export const isValidPlate = (value: string, country: string) => {
  const p = plateDigits(value, country === "US" ? 8 : 7);
  return country === "US" ? /^[A-Z0-9]{2,8}$/.test(p) : /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(p);
};

/** Máscara de CPF enquanto digita: 111444777 → 111.444.777 */
export const cpfMask = (value: string) => {
  const d = value.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

/** Confere o dígito verificador — o mesmo cálculo de apps/common/validators.py.
 *
 * Duplicar a regra no cliente é intencional: o profissional digita o CPF no
 * celular, e descobrir o erro só depois do POST custa uma ida ao
 * servidor no meio de um cadastro que ele faz entre dois serviços. O
 * backend continua sendo a autoridade. */
export const isValidCpf = (value: string) => {
  const cpf = value.replace(/\D/g, "");
  if (cpf.length !== 11 || cpf === cpf[0].repeat(11)) return false;
  for (const tamanho of [9, 10]) {
    let soma = 0;
    for (let i = 0; i < tamanho; i++) soma += Number(cpf[i]) * (tamanho + 1 - i);
    let digito = (soma * 10) % 11;
    if (digito === 10) digito = 0;
    if (digito !== Number(cpf[tamanho])) return false;
  }
  return true;
};

/** Máscara que muda de forma sozinha: CPF até 11 dígitos, CNPJ acima. */
export const documentMask = (value: string) => {
  const d = value.replace(/\D/g, "").slice(0, 14);
  if (d.length <= 11) return cpfMask(d);
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

/** Dígito verificador do CNPJ — mesma regra de apps/common/validators.py.
 *
 * Vale o mesmo argumento do CPF: o dono digita isso no celular, na tela
 * de pagamento, e receber "CNPJ inválido" só depois de uma ida ao
 * servidor (ou pior, do gateway) parece defeito do sistema. */
export const isValidCnpj = (value: string) => {
  const cnpj = value.replace(/\D/g, "");
  if (cnpj.length !== 14 || cnpj === cnpj[0].repeat(14)) return false;
  const pesos = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  for (const tamanho of [12, 13]) {
    const usados = pesos.slice(-tamanho);
    let soma = 0;
    for (let i = 0; i < tamanho; i++) soma += Number(cnpj[i]) * usados[i];
    const resto = soma % 11;
    const digito = resto < 2 ? 0 : 11 - resto;
    if (digito !== Number(cnpj[tamanho])) return false;
  }
  return true;
};

/** CPF ou CNPJ: a oficina de um box só assina com CPF. */
export const isValidDocument = (value: string) => {
  const d = value.replace(/\D/g, "");
  if (d.length === 11) return isValidCpf(d);
  if (d.length === 14) return isValidCnpj(d);
  return false;
};

/** Documento do titular, na forma do país.
 *
 * Espelha `TaxIdSpec` de apps/common/regions.py. Existe porque a dupla
 * `documentMask`/`isValidDocument` é CPF/CNPJ e só: aplicada a um EIN ela
 * escrevia `12-3456789` como `123.456.789`, reprovava na validação e
 * ainda deixava o botão Salvar desabilitado — nove dígitos nunca chegam
 * ao mínimo de onze do CPF. Na prática a oficina americana **não
 * conseguia** informar o documento dela.
 *
 * O backend já mandava `document_label`, `document_example` e
 * `document_required` no payload da cobrança; o formulário é que ignorava
 * os três. */
export const taxIdMask = (value: string, country: string) => {
  if (country !== "BR") {
    // EIN: 9 dígitos, hífen depois dos dois primeiros. Sem dígito
    // verificador — nos EUA não há o que conferir.
    const d = value.replace(/\D/g, "").slice(0, 9);
    return d.length > 2 ? `${d.slice(0, 2)}-${d.slice(2)}` : d;
  }
  return documentMask(value);
};

/** Vazio passa quando o país não exige — um campo opcional que recusa o
 *  vazio é um campo obrigatório com outro nome. Mesma regra de
 *  `_is_valid_ein`. */
export const isValidTaxId = (value: string, country: string, required: boolean) => {
  const d = value.replace(/\D/g, "");
  if (!d) return !required;
  return country === "BR" ? isValidDocument(d) : d.length === 9;
};

/** Normaliza para E.164, que é o formato que o backend guarda.
 *
 *  O prefixo vem do país da oficina. Decidir pelo comprimento, e não por
 *  "já começa com o código", evita o caso do DDD 55 (Santa Maria): um
 *  número de lá começa com 55 e não tem código de país nenhum. */
export const toE164 = (value: string, country: string) => {
  const digits = value.replace(/\D/g, "");
  if (country === "US") {
    if (digits.length === 11 && digits.startsWith("1")) return `+${digits}`;
    return `+1${digits}`;
  }
  if (digits.length > 11) return `+${digits}`;
  return `+55${digits}`;
};

export const isoDate = (d: Date) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60_000);
  return local.toISOString().slice(0, 10);
};

export const addDays = (d: Date, n: number) => {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
};

/** O domingo da semana de `d`.
 *
 *  Domingo cravado, e não descoberto: os dois idiomas do painel — pt-BR e
 *  en-US — começam a semana nele. `new Intl.Locale(x).getWeekInfo()`
 *  responde isso por locale e é a porta de entrada quando entrar um
 *  terceiro (a Europa começa na segunda), mas ainda não está em todo
 *  navegador que a recepção usa. Uma semana em silêncio deslocada é pior
 *  do que uma convenção declarada. */
export const startOfWeek = (d: Date) => addDays(d, -d.getDay());

export const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1);

export const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0);

/** Anda `n` meses e cai sempre no dia 1º.
 *
 *  Preservar o dia do mês é o que quebra: `setMonth(+1)` em 31 de março
 *  pede 31 de abril, o JS normaliza para 1º de maio, e abril desaparece
 *  de quem navega mês a mês. A visão é do mês inteiro — o dia da âncora
 *  não significa nada nela. */
export const addMonths = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth() + n, 1);

/** Os 42 dias da grade do mês, do domingo ao sábado.
 *
 *  As pontas caem no mês vizinho de propósito: a primeira linha começa
 *  no domingo, então dia 1º numa quarta deixa três células antes dele —
 *  e elas mostram os dias que realmente estão ali, não buracos.
 *
 *  Seis linhas fixas porque cinco não cabem todo mês (um mês de 31 dias
 *  que começa no sábado ocupa seis), e a grade não pode mudar de altura
 *  entre setembro e outubro: o clique do usuário viajaria junto. */
export const monthGrid = (d: Date) => {
  const inicio = startOfWeek(startOfMonth(d));
  return Array.from({ length: 42 }, (_, i) => addDays(inicio, i));
};

/** "setembro de 2026" · "September 2026" */
export const monthLabel = (d: Date, locale: string) =>
  d.toLocaleDateString(locale, { month: "long", year: "numeric" });

/** "14 – 20 de set." · "Sep 14 – 20"
 *
 *  `formatRange` é quem sabe que o inglês não repete o mês e o português
 *  não repete o "de". Escrever `${a} – ${b}` daria o intervalo certo nos
 *  dois idiomas e desajeitado nos dois. */
export const rangeLabel = (from: Date, to: Date, locale: string) =>
  new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short" }).formatRange(from, to);

/** Os sete nomes curtos na ordem da grade, começando no domingo.
 *
 *  `weekdayName` conta a partir da segunda porque essa é a convenção de
 *  `WorkingHours.weekday` no banco. A grade do calendário começa no
 *  domingo. Converter entre as duas aqui, uma vez, evita um `(i + 6) % 7`
 *  solto no meio do JSX — que é onde ele passa despercebido e desloca a
 *  semana inteira em uma coluna.
 *
 *  2024-01-07 foi um domingo. Meio-dia UTC para que nenhum fuso do mundo
 *  empurre a data para o dia anterior. */
export const weekdayHeaders = (locale: string, style: "short" | "narrow" = "short") =>
  Array.from({ length: 7 }, (_, i) =>
    new Date(Date.UTC(2024, 0, 7 + i, 12)).toLocaleDateString(locale, {
      weekday: style,
      timeZone: "UTC",
    }),
  );

/** Só a cor. O rótulo saiu daqui e foi para o dicionário
 *  (`status.pending` e companhia): uma tabela de estilo não é lugar de
 *  texto que precisa mudar de idioma. */
export const STATUS_STYLE: Record<string, string> = {
  pending: "border-amber-700/50 bg-amber-950/50 text-amber-300",
  confirmed: "border-racing-600/40 bg-racing-500/10 text-racing-400",
  in_progress: "border-violet-700/50 bg-violet-950/50 text-violet-300",
  completed: "border-emerald-800/50 bg-emerald-950/50 text-emerald-300",
  canceled: "border-line bg-carbon-700 text-steel-dim",
  no_show: "border-red-900/60 bg-red-950/50 text-red-300",
};
